/* eslint-disable react/jsx-props-no-spreading */
import { forwardRef } from 'react';
import Select, { components } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/pro-solid-svg-icons';

// Styles
import styles from './Select.module.css';

// Utils
import cn from '../../../utils/cn';

// Components
import Picto from '../Picto/Picto';

/**
 * Select component with color indicator support
 *
 * This component displays color indicators as FontAwesome square icons (fa-square)
 * instead of background colors for options that have a `color` property.
 *
 * How it works:
 * - Options with a `color` property will display a colored square icon before the label
 * - The color indicator appears in:
 *   1. OptionUnit: dropdown menu options
 *   2. SingleValue: selected value in the field
 *   3. MultiValueLabel: selected tags in multi-select mode
 *
 * Usage:
 * Pass options with a `color` property:
 *   { value: '1', label: 'Option', color: '#FF0000' }
 *
 * Components like EnumField, SelectClassField automatically pass the color property
 * from their options to this Select component.
 */

/**
 * @typedef {object} SelectOption
 * @property {string} label - Option label
 * @property {string} value - Option value
 */

/** @type {import('react-select').StylesConfig} */
const stylesSelect = {
  indicatorSeparator: () => ({ display: 'none' }),
  valueContainer: (provided) => ({
    ...provided,
    overflow: 'visible',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
    position: 'absolute',
    right: 0,
  }),
};

/**
 * @param {import('react-select').ValueContainerProps<any, false>} props
 * @returns {JSX.Element}
 */
function ValueContainer(props) {
  const { children, ...innerProps } = props;
  return (
    <components.ValueContainer {...innerProps}>
      {children}
    </components.ValueContainer>
  );
}

/**
 * Dropdown indicator component
 * @param {import('react-select').DropdownIndicatorProps} props
 * @returns {JSX.Element}
 */
function DropdownIndicator(props) {
  const { isFocused } = props;
  return (
    <components.DropdownIndicator {...props}>
      <Picto
        icon={isFocused ? 'unfold-less' : 'unfold-more'}
        className={styles.dropdownIndicator}
      />
    </components.DropdownIndicator>
  );
}
/**
 * Clear indicator component
 * @param {import('react-select').ClearIndicatorProps} props
 * @returns {JSX.Element}
 */
function ClearIndicator(props) {
  return (
    <components.ClearIndicator {...props}>
      <Picto icon="close-md" className={styles.clearIndicator} />
    </components.ClearIndicator>
  );
}

/**
 * Option component
 * @param {import('react-select').OptionProps} props
 * @returns {JSX.Element}
 */
function OptionUnit(props) {
  const { data: { label, color } } = props;
  const hasColor = color && color !== 'transparent';
  return (
    <components.Option {...props}>
      <div className={styles.option}>
        {hasColor && (
          <FontAwesomeIcon
            icon={faSquare}
            className={styles['option-color-icon']}
            style={{ color }}
            aria-label={`Couleur: ${color}`}
          />
        )}
        <span className={styles['option-label']}>{label}</span>
      </div>
    </components.Option>
  );
}

/**
 * SingleValue component
 * @param {import('react-select').SingleValueProps} props
 * @returns {JSX.Element}
 */
function SingleValue(props) {
  const { data, children } = props;
  const { color } = data || {};
  const hasColor = color && color !== 'transparent';
  return (
    <components.SingleValue {...props}>
      <span className={styles['single-value-container']}>
        {hasColor && (
          <FontAwesomeIcon
            icon={faSquare}
            className={styles['single-value-color-icon']}
            style={{ color }}
            aria-label={`Couleur: ${color}`}
          />
        )}
        <span className={styles['single-value-label']}>{children}</span>
      </span>
    </components.SingleValue>
  );
}

/**
 * Placeholder component
 * @param {import('react-select').PlaceholderProps} props
 * @returns {JSX.Element}
 */
function Placeholder(props) {
  return <components.Placeholder {...props} />;
}

/**
 * MultiValue component
 * @param {import('react-select').MultiValueProps} props
 * @returns {JSX.Element}
 */
function MultiValue(props) {
  const { children, ...restProps } = props;
  return (
    <components.MultiValue {...restProps}>
      <div className={styles['multi-value']}>
        {children}
      </div>
    </components.MultiValue>
  );
}

/**
 * MultiValueLabel component
 * @param {import('react-select').MultiValueGenericProps} props
 * @returns {JSX.Element}
 */
function MultiValueLabel(props) {
  const { children, data, ...restProps } = props;
  const { color } = data || {};
  const hasColor = color && color !== 'transparent';
  return (
    <components.MultiValueLabel {...restProps} data={data}>
      <span className={styles['multi-value-label']}>
        {hasColor && (
          <FontAwesomeIcon
            icon={faSquare}
            className={styles['multi-value-color-icon']}
            style={{ color }}
            aria-label={`Couleur: ${color}`}
          />
        )}
        {children}
      </span>
    </components.MultiValueLabel>
  );
}

/**
 * MultiValueRemove component
 * @param {import('react-select').MultiValueRemoveProps} props
 * @returns {JSX.Element}
 */
function MultiValueRemove(props) {
  return (
    <components.MultiValueRemove {...props}>
      <Picto icon="close-md" className={styles['multi-value-remove']} />
    </components.MultiValueRemove>
  );
}

/**
 * Group component
 * @param {import('react-select').GroupProps} props
 * @returns {JSX.Element}
 */
function Group(props) {
  const { children, ...restProps } = props;
  const label = restProps.label || '';
  return (
    <components.Group {...restProps}>
      <div className={styles.group}>
        {label ? (
          <div className={styles['group-heading']}>
            {label}
          </div>
        ) : null}
        <div className={styles['group-options']}>
          {children}
        </div>
      </div>
    </components.Group>
  );
}

const SelectField = forwardRef(
  /**
   * Select component
   * @param {object} props
   * @param {string} props.id
   * @param {string | SelectOption[]} props.value
   * @param {(value: SelectOption) => void} props.onChange
   * @param {(e: React.FocusEvent<HTMLInputElement>) => void} [props.onBlur] - Blur handler
   * @param {import('react-select').OptionsOrGroups<SelectOption, import('react-select').GroupBase<SelectOption>>} props.options - Options array (simple or grouped) using official react-select types
   * @param {boolean} [props.disabled]
   * @param {(props: import('react-select').OptionProps) => JSX.Element} [props.customOption]
   * @param {(props: import('react-select').ValueContainerProps) => JSX.Element} [props.customValueContainer]
   * @param {(props: import('react-select').MultiValueProps) => JSX.Element} [props.customMultiValue]
   * @param {(props: import('react-select').MultiValueProps) => JSX.Element} [props.customMultiValueLabel]
   * @param {(props: import('react-select').MultiValueRemoveProps) => JSX.Element} [props.customMultiValueRemove]
   * @param {string} [props.theme]
   * @param {'small' | 'medium'} [props.size]
   * @param {string} [props.placeholder]
   * @param {boolean} [props.isMulti]
   * @param {string} [props.error]
   * @param {string} [props.className]
   * @param {boolean} [props.isDisabled]
   * @param {boolean} [props.isReadOnly] - Locked display; value stays in form (not isDisabled)
   * @param {boolean} [props.isLoading]
   * @param {(option: SelectOption) => boolean} [props.isOptionDisabled] - Function to determine if an option should be disabled
   * @param {SelectOption} [props.customSelectAllOption] - Custom select all option
   * @param {boolean} [props.menuIsOpen] - Control whether the menu is open (controlled component)
   * @param {() => void} [props.onMenuOpen] - Callback when menu opens
   * @param {() => void} [props.onMenuClose] - Callback when menu closes
   * @param {boolean} [props.isClearable] - Passed through to react-select
   * @param {boolean} [props.escapeClearsValue] - Passed through to react-select
   * @param {(e: import('react').KeyboardEvent<HTMLDivElement>) => void} [props.onKeyDown] - Passed through to react-select
   * @param {React.Ref<any>} ref
   * @returns {JSX.Element}
   */
  (props, ref) => {
    const {
      disabled,
      id,
      customOption = OptionUnit,
      customValueContainer = ValueContainer,
      customMultiValue = MultiValue,
      customMultiValueLabel = MultiValueLabel,
      customMultiValueRemove = MultiValueRemove,
      isDisabled = false,
      isReadOnly = false,
      isLoading = false,
      theme,
      size,
      options,
      value,
      placeholder,
      isMulti = false,
      error = '',
      className = '',
      customSelectAllOption,
      onBlur,
      menuIsOpen,
      onMenuOpen,
      onMenuClose,
      onChange,
      isClearable,
      ...restProps
    } = props;

    const OptionComponent = typeof customOption === 'function' ? customOption : OptionUnit;
    const ValueContainerComponent = typeof customValueContainer === 'function' ? customValueContainer : ValueContainer;
    const MultiValueComponent = typeof customMultiValue === 'function' ? customMultiValue : MultiValue;
    const MultiValueLabelComponent = typeof customMultiValueLabel === 'function' ? customMultiValueLabel : MultiValueLabel;
    const MultiValueRemoveComponent = typeof customMultiValueRemove === 'function' ? customMultiValueRemove : MultiValueRemove;

    const activeOption = options?.flatMap((group) => {
      if ('options' in group) return group.options;
      return group;
    }).find((option) => option.value === value);

    // Add custom select all option if it exists
    const optionsWithSelectAll = isMulti && customSelectAllOption ? [
      customSelectAllOption,
      ...options,
    ] : options;

    const locked = isReadOnly && !disabled && !isDisabled;

    return (
      <div className={cn([
        styles.container,
        (disabled || isDisabled) && styles.disabled,
        locked && styles['read-only'],
        isLoading && styles.loading,
        styles[theme],
        styles[size],
        error && styles['has-error'],
      ])}
      >
        <Select
          instanceId={id}
          classNamePrefix="react-select"
          styles={stylesSelect}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          options={optionsWithSelectAll}
          value={isMulti ? value : activeOption}
          isMulti={isMulti}
          closeMenuOnSelect={!isMulti}
          components={{
            Option: OptionComponent,
            DropdownIndicator,
            ...(isMulti ? { ClearIndicator: undefined } : { ClearIndicator }),
            ValueContainer: ValueContainerComponent,
            SingleValue,
            Placeholder,
            MultiValue: MultiValueComponent,
            MultiValueLabel: MultiValueLabelComponent,
            MultiValueRemove: MultiValueRemoveComponent,
            Group,
          }}
          noOptionsMessage={() => 'Aucune option disponible'}
          ref={ref}
          placeholder={placeholder}
          className={cn([styles['css-container'], className])}
          onBlur={(e) => {
            onBlur?.(e);
          }}
          menuIsOpen={locked ? false : menuIsOpen}
          onMenuOpen={locked ? undefined : onMenuOpen}
          onMenuClose={onMenuClose}
          openMenuOnClick={!locked}
          isDisabled={disabled || isDisabled}
          isClearable={locked ? false : isClearable}
          onChange={locked ? () => {} : onChange}
          {...restProps}
        />

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}
      </div>
    );
  },
);

SelectField.displayName = 'SelectField';

export default SelectField;
